import { useState } from 'react';
import {
  Search,
  MoreVertical,
  Edit,
  Trash2,
  AlertTriangle,
  Loader2,
  Link2,
  ExternalLink,
  Copy,
  Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AdminLayout } from '@/components/admin';
import { Card, Button, Badge, PlatformIcon } from '@/components/ui';
import { useAdminDeeplinks } from '@/hooks/useAdminDeeplinks';
import type { DeeplinkWithOwner } from '@/services/admin';

const AdminDeeplinks = () => {
  const { t } = useTranslation();
  const {
    deeplinks,
    loading,
    error,
    search,
    total,
    handleSearch,
    update,
    remove,
  } = useAdminDeeplinks();

  const [searchInput, setSearchInput] = useState(search);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [editModal, setEditModal] = useState<DeeplinkWithOwner | null>(null);
  const [deleteModal, setDeleteModal] = useState<DeeplinkWithOwner | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchInput);
  };

  const handleCopy = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(`https://klipr.it/${code}`);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleEdit = async () => {
    if (!editModal) return;
    setActionLoading(editModal.id);
    try {
      await update(editModal.id, { name: editName });
      setEditModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal) return;
    setActionLoading(deleteModal.id);
    try {
      await remove(deleteModal.id);
      setDeleteModal(null);
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp: unknown) => {
    if (!timestamp) return '-';
    const date = (timestamp as { toDate: () => Date }).toDate?.() || new Date(timestamp as string);
    return date.toLocaleDateString();
  };

  const openEditModal = (deeplink: DeeplinkWithOwner) => {
    setEditName(deeplink.name || '');
    setEditModal(deeplink);
    setOpenDropdown(null);
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-dark-800 dark:text-white mb-2">
          {t('admin.deeplinksTitle')}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          {t('admin.deeplinksSubtitle', { count: total })}
        </p>
      </div>

      {/* Search */}
      <Card variant="default" padding="md" className="mb-6">
        <form onSubmit={handleSearchSubmit} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('admin.searchDeeplinksPlaceholder')}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:text-white dark:placeholder-gray-400"
            />
          </div>
          <Button type="submit" variant="primary">
            {t('common.search')}
          </Button>
        </form>
      </Card>

      {/* Error */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Deeplinks Table */}
      <Card variant="default" padding="none">
        {loading ? (
          <div className="p-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">{t('common.loading')}</p>
          </div>
        ) : deeplinks.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Link2 className="w-6 h-6 text-gray-400" />
            </div>
            <p className="text-gray-500 dark:text-gray-400">{t('admin.noDeeplinksFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.name')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.code')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.platform')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.status')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.owner')}
                  </th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.created')}
                  </th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t('admin.actions')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {deeplinks.map((deeplink) => (
                  <tr key={deeplink.id} className="border-b border-gray-50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <PlatformIcon platform={deeplink.platform} size="sm" />
                        <div className="min-w-0">
                          <p className="font-medium text-dark-800 dark:text-white truncate max-w-[200px]">
                            {deeplink.name || `klipr.it/${deeplink.code}`}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[200px]">
                            {deeplink.originalUrl}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-lime-600 dark:text-lime-400 font-mono">{deeplink.code}</span>
                        <button
                          onClick={() => handleCopy(deeplink.code, deeplink.id)}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          {copiedId === deeplink.id ? (
                            <Check className="w-4 h-4 text-green-500" />
                          ) : (
                            <Copy className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                        <a
                          href={`https://klipr.it/${deeplink.code}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <ExternalLink className="w-4 h-4 text-gray-400" />
                        </a>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant="lavender" size="sm">
                        {deeplink.platform}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      {deeplink.status === 'rejected' ? (
                        <Badge variant="error" size="sm">
                          {t('admin.statusRejected')}
                        </Badge>
                      ) : deeplink.status === 'disabled' ? (
                        <Badge variant="dark" size="sm">
                          {t('admin.statusDisabled')}
                        </Badge>
                      ) : deeplink.checkNeeded ? (
                        <Badge variant="warning" size="sm">
                          {t('admin.statusMonitored')}
                        </Badge>
                      ) : (
                        <Badge variant="success" size="sm">
                          {t('admin.statusVerified')}
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="min-w-0">
                        <p className="text-sm text-dark-800 dark:text-white truncate">
                          {deeplink.ownerDisplayName || '-'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {deeplink.ownerEmail}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                      {formatDate(deeplink.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end relative">
                        {actionLoading === deeplink.id ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <>
                            <button
                              onClick={() => setOpenDropdown(openDropdown === deeplink.id ? null : deeplink.id)}
                              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
                            >
                              <MoreVertical className="w-5 h-5 text-gray-400" />
                            </button>

                            {openDropdown === deeplink.id && (
                              <>
                                <div
                                  className="fixed inset-0 z-10"
                                  onClick={() => setOpenDropdown(null)}
                                />
                                <div className="absolute right-0 top-full mt-1 w-40 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 py-1 z-20">
                                  <button
                                    onClick={() => openEditModal(deeplink)}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                  >
                                    <Edit className="w-4 h-4" />
                                    {t('common.edit')}
                                  </button>

                                  <hr className="my-1 border-gray-100 dark:border-gray-700" />

                                  <button
                                    onClick={() => {
                                      setOpenDropdown(null);
                                      setDeleteModal(deeplink);
                                    }}
                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                    {t('common.delete')}
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setEditModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-dark-800 dark:text-white mb-4">
              {t('admin.editDeeplink')}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('admin.deeplinkName')}
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={`klipr.it/${editModal.code}`}
                className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 dark:text-white dark:placeholder-gray-400"
              />
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4 mb-6">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{t('admin.originalUrl')}</p>
              <p className="text-sm text-dark-800 dark:text-white truncate">{editModal.originalUrl}</p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setEditModal(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleEdit}
                disabled={actionLoading === editModal.id}
              >
                {actionLoading === editModal.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('common.save')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => setDeleteModal(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-3xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-2xl flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-800 dark:text-white">
                  {t('admin.deleteDeeplinkConfirmTitle')}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('admin.deleteDeeplinkConfirmDescription')}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-2xl p-4 mb-6">
              <div className="flex items-center gap-3">
                <PlatformIcon platform={deleteModal.platform} size="sm" />
                <div>
                  <p className="font-medium text-dark-800 dark:text-white">
                    {deleteModal.name || `klipr.it/${deleteModal.code}`}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{deleteModal.code}</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={() => setDeleteModal(null)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="danger"
                className="flex-1"
                onClick={handleDelete}
                disabled={actionLoading === deleteModal.id}
              >
                {actionLoading === deleteModal.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t('common.delete')
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDeeplinks;
